import React from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { PhoneInput } from './PhoneInput';
import { AlertCircle } from 'lucide-react';
import { useCurrency } from '../contexts/CurrencyContext';

export function LeadFormSheet({
  open,
  onOpenChange,
  editingLead,
  formData,
  setFormData,
  onSubmit,
  submitting,
  pipelineStages,
  leadSources,
  teamMembers,
  duplicateError,
}) {
  const currency = useCurrency();
  const navigate = useNavigate();
  const [catalogItems, setCatalogItems] = React.useState([]);

  React.useEffect(() => {
    if (!open) return;
    api.get('/api/inventory/items', { params: { is_active: true, limit: 1000 } })
      .then((r) => setCatalogItems(r.data?.data || []))
      .catch(() => setCatalogItems([]));
  }, [open]);

  const handlePhoneChange = (phone, countryInfo) => {
    const updates = { ...formData, phone };
    if (countryInfo) {
      updates.country = countryInfo.name;
      updates.phone_country_code = countryInfo.code;
    }
    setFormData(updates);
  };

  const isEmailDuplicate = duplicateError && duplicateError.toLowerCase().includes('email');
  const isPhoneDuplicate = duplicateError && duplicateError.toLowerCase().includes('phone');

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="overflow-y-auto sm:max-w-lg" data-testid="lead-dialog">
        <SheetHeader>
          <SheetTitle>{editingLead ? 'Edit Lead' : 'Create Lead'}</SheetTitle>
        </SheetHeader>
        <form onSubmit={onSubmit} className="space-y-4 mt-6">
          {duplicateError && (
            <div className="flex items-start gap-2 p-3 rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-800/50" data-testid="duplicate-error-banner">
              <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
              <p className="text-sm text-red-700 dark:text-red-400">{duplicateError}</p>
            </div>
          )}
          <div>
            <Label htmlFor="name">Name *</Label>
            <Input id="name" value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required data-testid="lead-name-input" />
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className={isEmailDuplicate ? 'border-red-500 focus-visible:ring-red-500' : ''}
              data-testid="lead-email-input" />
            {isEmailDuplicate && (
              <p className="text-xs text-red-600 mt-1" data-testid="email-duplicate-hint">This email is already in use by another lead.</p>
            )}
          </div>
          <div>
            <Label htmlFor="phone">Phone</Label>
            <div className={isPhoneDuplicate ? '[&_input]:border-red-500' : ''}>
              <PhoneInput
                value={formData.phone}
                onChange={handlePhoneChange}
                defaultCountryCode={formData.phone_country_code || '+91'}
              />
            </div>
            {isPhoneDuplicate && (
              <p className="text-xs text-red-600 mt-1" data-testid="phone-duplicate-hint">This phone number is already in use by another lead.</p>
            )}
          </div>
          <div>
            <Label htmlFor="company">Company</Label>
            <Input id="company" value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              data-testid="lead-company-input" />
          </div>
          <div>
            <Label htmlFor="website">Company Website</Label>
            <Input id="website" value={formData.website || ''}
              placeholder="acme.com"
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              data-testid="lead-website-input" />
            <p className="text-xs text-muted-foreground mt-1">Improves AI enrichment &amp; lead scoring accuracy.</p>
          </div>
          <div>
            <Label htmlFor="stage">Stage</Label>
            <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
              <SelectTrigger data-testid="lead-stage-select"><SelectValue placeholder="Select stage" /></SelectTrigger>
              <SelectContent>
                {pipelineStages.map((stage) => (
                  <SelectItem key={stage.id} value={stage.name}>{stage.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="value">Expected Value ({currency.symbol})</Label>
            <Input id="value" type="tel" inputMode="decimal"
              onInput={(e) => { e.target.value = e.target.value.replace(/[^0-9.]/g, ""); }}
              value={formData.value}
              onChange={(e) => setFormData({ ...formData, value: parseFloat(e.target.value) || 0 })}
              data-testid="lead-value-input" />
          </div>
          <div>
            <Label htmlFor="source">Source</Label>
            <Select value={formData.source} onValueChange={(value) => setFormData({ ...formData, source: value })}>
              <SelectTrigger data-testid="lead-source-select"><SelectValue placeholder="Select source" /></SelectTrigger>
              <SelectContent>
                {leadSources.map((source) => (
                  <SelectItem key={source.id} value={source.name}>{source.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="interested_product">Product / Service interest <span className="text-muted-foreground text-xs font-normal">(optional — AI auto-matches if blank)</span></Label>
            <Select
              value={formData.interested_product_id || '__none__'}
              onValueChange={(value) => {
                if (value === '__add_new__') { navigate('/profile?tab=products'); return; }
                setFormData({ ...formData, interested_product_id: value === '__none__' ? '' : value });
              }}
            >
              <SelectTrigger data-testid="lead-product-select">
                <SelectValue placeholder={catalogItems.length ? 'Let AI decide (recommended)' : 'No products/services in catalog yet'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Let AI decide (recommended)</SelectItem>
                {formData.interested_product_id
                  && !catalogItems.some((it) => it.id === formData.interested_product_id) && (
                  <SelectItem value={formData.interested_product_id} data-testid="lead-product-current">
                    {formData.interested_product_name || 'Current selection'}
                  </SelectItem>
                )}
                {catalogItems.map((item) => (
                  <SelectItem key={item.id} value={item.id} data-testid={`lead-product-option-${item.id}`}>{item.name}</SelectItem>
                ))}
                <SelectItem value="__add_new__" data-testid="lead-product-add-new">+ Add new product/service…</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="assigned_to">Assigned To</Label>
            <Select value={formData.assigned_to} onValueChange={(value) => setFormData({ ...formData, assigned_to: value })}>
              <SelectTrigger data-testid="lead-assigned-select"><SelectValue placeholder="Select team member" /></SelectTrigger>
              <SelectContent>
                {teamMembers.map((member) => (
                  <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="notes">Notes</Label>
            <Textarea id="notes" value={formData.notes || ''}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              data-testid="lead-notes-input" />
          </div>
          <div className="flex gap-2">
            <Button type="submit" disabled={submitting} data-testid="lead-submit-button">
              {submitting ? 'Saving...' : (editingLead ? 'Update' : 'Create')}
            </Button>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
